import React, { useState } from "react";

import { ERRORED_CLASSNAME } from "@impero-com/common/utils/form/erroredElements";
import { FormField, FormLabel } from "@impero-com/ui";
import { Typography } from "antd";

import { SavedBlob } from "api/generated/model";
import { FileUploader } from "components/fileUploader/fileUploader";
import { DeleteBlobAction } from "components/fileUploader/queries/useDeleteBlobMutation";
import { UploaderFile } from "components/fileUploader/types";
import { savedBlobToUploaderFile } from "components/fileUploader/utils";

import * as theme from "./theme.css";

const { Text } = Typography;

export interface FileUploaderInputBaseProps {
    preUploadCallback?: () => Promise<any>;
    emptyText?: string;
    label?: React.ReactNode;
    deleteAction?: DeleteBlobAction;
    hideMissing?: boolean;
    savedBlobs?: SavedBlob[];
    disabled?: boolean;
    /**
     * Maximum number of files that can be uploaded. If not specified, there is no limit.
     */
    maxFiles?: number;
}

interface FileUploaderInputProps extends FileUploaderInputBaseProps {
    error?: string;
    onChange?: (value: string[]) => void;
    onBlur?: () => void;
    fieldRef?: React.RefCallback<HTMLElement>;
    // Array of blob ids to filter which are shown
    value?: string[];
}
export function FileUploaderInput({
    value,
    preUploadCallback,
    error,
    emptyText,
    label,
    fieldRef,
    savedBlobs,
    onChange,
    disabled,
    deleteAction = { kind: "DELETE_BLOB" },
    hideMissing = false,
    maxFiles = undefined,
}: FileUploaderInputProps) {
    const shownBlobs = savedBlobs?.filter((blob) => value?.includes(blob.id));
    const [files, setFiles] = useState<UploaderFile[]>(() =>
        shownBlobs
            ? shownBlobs.map((savedBlob) => savedBlobToUploaderFile(savedBlob))
            : []
    );
    const hasFileErrors = files.some((file) => file.status === "error");

    const handleSetValue = (
        setFilesFn: (prev: UploaderFile[]) => UploaderFile[]
    ) => {
        if (!onChange) {
            return;
        }
        setFiles((prev) => {
            const newFiles = setFilesFn(prev);
            const successFiles = newFiles.filter(
                (file) => file.status === "success" || file.status === "done"
            );
            const blobIds = successFiles.map((file) => file.id);
            onChange(blobIds);
            return newFiles;
        });
    };

    return (
        <FormField>
            {!!label && <FormLabel disabled={disabled}>{label}</FormLabel>}

            <div className={theme.fileUploaderInputLayout}>
                <FileUploader
                    value={files}
                    setValue={handleSetValue}
                    preUploadCallback={preUploadCallback}
                    disabled={disabled}
                    emptyText={emptyText}
                    deleteAction={deleteAction}
                    hideMissing={hideMissing}
                    maxFiles={maxFiles}
                    uploaderRef={fieldRef}
                />
                {(hasFileErrors || error) && (
                    <Text className={ERRORED_CLASSNAME} type="danger">
                        {error}
                    </Text>
                )}
            </div>
        </FormField>
    );
}
